import { FormType } from 'src/containers/Form';
import { GithubWebsiteDeployFormShape } from './DeployToWebsite';
import { Box } from 'src/components/Box';
import { useCallback, useEffect, useState } from 'react';
import { GitRepositoryRefs, PersistentAppKind, WebsiteGithubConnectionRefKindEnum } from 'src/orchd-client';
import { orgsApi } from 'src/api_services/orgs/service';
import { SelectField } from 'src/components/Select';
import { SelectLabelWithIcon } from 'src/components/SelectLabelWithIcon/SelectLabelWithIcon';
import { Text } from 'src/components/Text';
import { FormattedMessage } from 'react-intl';
import { TextInputField } from 'src/components/TextInput';
import { TextAreaField } from 'src/components/TextArea/TextAreaField';
import FormField from 'src/components/FormField';
import { Toggle } from 'src/components/Toggle';
import { SelectableRuntimes } from './SelectableRuntime';
import { SelectableNodeVersion } from './SelectableNodeVersion';
import { Checkbox } from 'src/components/Checkbox';
import { getStartupCommandForRuntime } from 'src/utils/runtime-start-command-mapping';

interface Props {
  form: FormType<GithubWebsiteDeployFormShape>;
  repoOption: {
    label: JSX.Element;
    value: string;
  };
  orgId: string;
  full_name: string;
  persistentAppsAllowed?: boolean;
}

export const StepTwo = ({ form, repoOption, full_name, orgId, persistentAppsAllowed }: Props) => {
  const [repoRefs, setRepoRefs] = useState<GitRepositoryRefs>({
    branches: [],
    tags: [],
  });

  const [refsLoading, setRefsLoading] = useState(true);

  const fetchRefs = useCallback(() => {
    orgsApi
      .getOrgGithubRepoRefs({
        orgId,
        repoName: full_name.split('/')[1],
        repoOwner: full_name.split('/')[0],
      })
      .then((data) => {
        setRepoRefs(data.data);
      })
      .finally(() => setRefsLoading(false));
  }, [orgId, full_name]);

  useEffect(() => {
    fetchRefs();
  }, [fetchRefs]);

  const repoRefsOptionsTags = repoRefs.tags.map((tag) => ({
    label: (
      <SelectLabelWithIcon icon="hash" iconSize="lg">
        <Text>{tag}</Text>
      </SelectLabelWithIcon>
    ),
    value: tag,
    data: {
      refKind: WebsiteGithubConnectionRefKindEnum.tag,
    },
  }));

  const repoRefsOptionsBranches = repoRefs.branches.map((branch) => ({
    label: (
      <SelectLabelWithIcon icon="github_branch" iconSize="lg">
        <Text>{branch}</Text>
      </SelectLabelWithIcon>
    ),
    value: branch,
    data: {
      refKind: WebsiteGithubConnectionRefKindEnum.branch,
    },
  }));

  const repoRefsOptions = [...repoRefsOptionsTags, ...repoRefsOptionsBranches];

  // Auto-select a default branch or tag once the refs have loaded:
  // prefer "main", then "master", then the first available option.
  useEffect(() => {
    if (refsLoading || form.values.branchOrTagName) {
      return;
    }
    const preferredRef =
      repoRefsOptionsBranches.find((option) => option.value === 'main') ||
      repoRefsOptionsBranches.find((option) => option.value === 'master') ||
      repoRefsOptions[0];
    if (!preferredRef) {
      return;
    }
    // Same as onChange: set refKind without running its own validation to
    // avoid its async SET_ERRORS landing after this field's validation.
    form.setFieldValue('refKind', preferredRef.data.refKind, false);
    form.setFieldValue('branchOrTagName', preferredRef.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refsLoading, repoRefs]);

  return (
    <Box pb="lg" d="row" g="lg">
      <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
        <SelectField
          options={[repoOption]}
          value={[repoOption]}
          name="repoDummy"
          label={<FormattedMessage id="git_deploy.repository" defaultMessage="Repository" />}
          onChange={() => {}}
          disabled
        />
      </Box>

      <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
        <SelectField
          name="branchOrTagName"
          value={[
            repoRefsOptions?.find((option) => option.value === form.values.branchOrTagName) || {
              label: undefined,
              value: undefined,
            },
          ]}
          options={repoRefsOptions}
          label={<FormattedMessage id="git_deploy.select_branch_tag_label" defaultMessage="Branch or tag" />}
          onChange={(field) => {
            // refKind is set without running its own validation: validating it here
            // would run against the pre-change snapshot (branchOrTagName still empty)
            // and its async SET_ERRORS can land after the branch validation, leaving
            // isValid stuck false until another field is touched.
            form.setFieldValue('refKind', field.value[0].data.refKind, false);
            form.setFieldValue(field.name, field.value[0].value);
          }}
          clearable={false}
          loading={refsLoading}
          placeholder={
            <FormattedMessage id="git_deploy.select_brand_tag_placeholder" defaultMessage="Select branch or tag" />
          }
          disabled={refsLoading}
        />
      </Box>

      <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
        <TextAreaField
          name="buildCommand"
          rows={4}
          help={
            <FormattedMessage
              id="git_deploy.new_website.build_script_help"
              defaultMessage="Runs from the deploy directory after the Git repository is cloned"
            />
          }
          value={form.values.buildCommand}
          onChange={form.handleChange}
          label={<FormattedMessage id="git_deploy.new_website.build_script" defaultMessage="Build script" />}
        />
      </Box>

      <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
        <TextInputField
          help="Directory relative to the deploy directory where static assets are built"
          name="publishDirectory"
          value={form.values.publishDirectory}
          onChange={form.handleChange}
          err={form.errors.publishDirectory?.toString()}
          label={<FormattedMessage id="git_deploy.new_website.publish_directory" defaultMessage="Publish directory" />}
        />
      </Box>

      <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
        <FormField
          label={
            <Box mb="sm">
              <FormattedMessage id="git_deploy.new_website.auto_deploy" defaultMessage="Auto deploy on push" />
            </Box>
          }
          name="autoDeploy"
        >
          <Toggle
            name="autoDeploy"
            onChange={() => form.setFieldValue('autoDeploy', !form.values.autoDeploy)}
            checked={form.values.autoDeploy}
          />
        </FormField>
      </Box>
      {persistentAppsAllowed && (
        <FormField
          name="persistentAppRuntime"
          label={
            <Box mb="xs">
              <FormattedMessage
                id="git_deploy.step_two.persistent_runtime_label"
                defaultMessage="Persistent runtime (worker process)"
              />
            </Box>
          }
          marginBottom="xl"
        >
          <SelectableRuntimes
            changeRuntime={(runtime) => {
              form.setFieldValue('persistentApp.appKind', runtime);
              if (runtime) {
                form.setFieldValue('persistentApp.command', getStartupCommandForRuntime(runtime));
              }
              if (runtime === undefined) {
                form.setFieldValue('persistentApp.proxyDetails', undefined);
              }
            }}
            currentRuntime={form.values.persistentApp?.appKind}
            showNoneOption={true}
          />
        </FormField>
      )}

      {persistentAppsAllowed && form.values.persistentApp?.appKind && (
        <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
          <TextInputField
            requiredIndicator
            label={<FormattedMessage id="nodejs.form_labels.command" defaultMessage="Startup command" />}
            name="persistentApp.command"
            value={form.values.persistentApp?.command}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            err={form.errors.persistentApp?.command}
            touched={form.touched.persistentApp?.command}
            help={
              <FormattedMessage
                id="nodejs.form_labels.startup_command_helper"
                defaultMessage="Enter the command used to start your application."
              />
            }
            placeholder="npm start"
          />
        </Box>
      )}
      {persistentAppsAllowed && form.values.persistentApp?.appKind === PersistentAppKind.nodejs && (
        <Box w={{ xs: 12, md: 6 }} d="column" mb="xl">
          <SelectableNodeVersion
            currentNodeVersion={form.values.persistentApp?.version}
            setCurrentNodeVersion={(version) => form.setFieldValue('persistentApp.version', version)}
            touched={form.touched.persistentApp?.version}
            error={form.errors.persistentApp?.version}
          />
        </Box>
      )}

      {persistentAppsAllowed && form.values.persistentApp?.appKind && (
        <Toggle
          fullWidth
          checked={form.values.persistentApp?.proxyDetails !== undefined}
          name="proxy_enabled"
          marginBottom="xl"
          onChange={(val) => {
            if (val.checked) {
              form.setFieldValue('persistentApp.proxyDetails', {
                path: '',
                port: 3000,
                allowWebSocketUpgrade: false,
              });
            } else {
              form.setFieldValue('persistentApp.proxyDetails', undefined);
            }
          }}
          rightLabel={
            <Box d="column">
              <Text size="sm">
                <FormattedMessage id="website.git_deploy.node_js.proxy_enabled" defaultMessage="HTTP Proxy" />
              </Text>{' '}
              <Text fullWidth size="sm" color="grey">
                <FormattedMessage
                  id="worker_process.proxy_guidance"
                  defaultMessage="The web server will forward traffic from your primary domain to your worker process."
                />
              </Text>
            </Box>
          }
        />
      )}
      {persistentAppsAllowed && form.values.persistentApp.proxyDetails && (
        <Box w={{ xs: 12, sm: 6 }} mb="xl">
          <TextInputField
            disabled={form.values.persistentApp?.proxyDetails === undefined}
            requiredIndicator={true}
            label={<FormattedMessage id="nodejs.form_labels.path" defaultMessage="Path" />}
            name="persistentApp.proxyDetails.path"
            value={form.values.persistentApp.proxyDetails?.path ?? ''}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            err={form.errors.persistentApp?.proxyDetails?.path}
            touched={form.touched.persistentApp?.proxyDetails?.path}
            prefix="/"
            help={
              <Box>
                <FormattedMessage
                  id="node_js.path_helper"
                  defaultMessage="Leave this blank to proxy the entire website."
                />
              </Box>
            }
          />
        </Box>
      )}
      {persistentAppsAllowed && form.values.persistentApp.proxyDetails && (
        <Box w={{ xs: 12, sm: 6 }} mb="xl">
          <TextInputField
            disabled={form.values.persistentApp?.proxyDetails === undefined}
            requiredIndicator={true}
            label={<FormattedMessage id="nodejs.form_labels.port" defaultMessage="Port" />}
            name="persistentApp.proxyDetails.port"
            value={
              form.values.persistentApp?.proxyDetails?.port
                ? form.values.persistentApp?.proxyDetails.port.toString()
                : ''
            }
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            err={form.errors.persistentApp?.proxyDetails?.port}
            touched={form.touched.persistentApp?.proxyDetails?.port}
            type="number"
            help={
              <Box>
                <FormattedMessage
                  id="node_js.port_helper_short"
                  defaultMessage="Your application should listen on 0.0.0.0."
                />
              </Box>
            }
          />
        </Box>
      )}
      {persistentAppsAllowed && form.values.persistentApp.proxyDetails && (
        <Box mb="xl">
          <Checkbox
            onChange={(e) => {
              form.setFieldValue('persistentApp.proxyDetails.allowWebSocketUpgrade', e.checked);
            }}
            label={
              <Box d="column">
                <FormattedMessage id="node_js.allow_web_socket_proxy" defaultMessage="Allow web socket proxy upgrade" />
                <Text fullWidth size="sm" italic color="grey">
                  <FormattedMessage
                    id="node_js.web_socket_proxy_helper"
                    defaultMessage="Pass through web socket requests to your backend application."
                  />
                </Text>
              </Box>
            }
            name="persistentApp.proxyDetails.allowWebSocketUpgrade"
            checked={form.values.persistentApp?.proxyDetails?.allowWebSocketUpgrade}
            size="sm"
            fontSize="sm"
            marginBottom="xs"
            inputBackgroundCheckedColor="accent"
            borderCheckedColor="accent"
          />
        </Box>
      )}
    </Box>
  );
};
